<template>
    <v-container fluid class="page-container">
        <page-header title="Transactions" :subtitle="totalLabel">
            <template v-if="isAdmin">
                <v-btn outlined color="primary" :loading="exporting" @click="exportTransactions">
                    <v-icon left>mdi-file-delimited-outline</v-icon>Export CSV
                </v-btn>
                <v-btn color="primary" depressed @click="createItem">
                    <v-icon left>mdi-plus</v-icon>New transaction
                </v-btn>
            </template>
        </page-header>

        <filter-panel
            :fields="filterFields"
            :value="listState"
            :loading="loading"
            @apply="applyFilters"
        />

        <v-row dense class="mb-3">
            <v-col cols="12" sm="6" md="3">
                <v-card outlined class="stat-card">
                    <div class="stat-label">Transactions</div>
                    <div class="stat-value">{{ total }}</div>
                </v-card>
            </v-col>
            <v-col cols="12" sm="6" md="3">
                <v-card outlined class="stat-card">
                    <div class="stat-label">Total receipt value</div>
                    <div class="stat-value">{{ formatCurrency(sum) }}</div>
                </v-card>
            </v-col>
        </v-row>

        <v-card outlined>
            <v-data-table
                :headers="visibleHeaders"
                v-bind="tableBindings"
                class="list-table clickable-rows"
                no-data-text="No transactions match these filters"
                @update:options="onTableOptions"
                @click:row="openItem($event)"
            >
                <template v-slot:[`item.date`]="{ item }">{{ item.receipt ? formatDate(item.receipt.date) : '' }}</template>
                <template v-slot:[`item.member`]="{ item }">
                    <span class="font-weight-medium">{{ memberName(item.member) }}</span>
                </template>
                <template v-slot:[`item.card`]="{ item }">{{ paronCards(item.member) }}</template>
                <template v-slot:[`item.partner`]="{ item }">{{ item.partner ? item.partner.title : '' }}</template>
                <template v-slot:[`item.category`]="{ item }">{{ item.partner ? item.partner.category : '' }}</template>
                <template v-slot:[`item.total`]="{ item }">
                    <span class="font-weight-medium">{{ item.receipt && item.receipt.total !== null ? formatCurrency(item.receipt.total) : '' }}</span>
                </template>
                <template v-slot:[`item.scanCompleted`]="{ item }">
                    <v-icon small :color="item.scanCompleted ? 'success' : 'error'" :title="item.scanCompleted ? 'Scan completed' : 'Scan pending'">
                        {{ item.scanCompleted ? 'mdi-check-circle' : 'mdi-clock-outline' }}
                    </v-icon>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <div class="d-flex justify-end">
                        <v-btn icon small color="primary" title="Edit" @click.stop="openItem(item, 'edit')">
                            <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                        <delete-button title="Delete this transaction?" @delete-entry="deleteItem(item, deleteTransaction, 'Transaction')" />
                    </div>
                </template>
            </v-data-table>
            <table-footer v-model="page" :page-count="pageCount" :total="total" :items-per-page="itemsPerPage" />
        </v-card>
    </v-container>
</template>

<script>
    import transactionService from '@/services/transaction-service'
    import dealerService from '@/services/dealer-service'
    import teamService from '@/services/team-service'
    import paginatedList from '@/mixins/paginated-list'
    import PageHeader from '@/components/PageHeader'
    import FilterPanel from '@/components/FilterPanel'
    import TableFooter from '@/components/TableFooter'
    import DeleteButton from '@/components/DeleteButton'
    import { activeCardNumbers, formatCurrency, formatDate, fullName, saveBlob, dateStamp } from '@/utils/format'

    export default {
        name: 'TransactionRoute',
        components: { PageHeader, FilterPanel, TableFooter, DeleteButton },
        mixins: [paginatedList],
        data() {
            return {
                scopeToDealer: true,
                sum: 0,
                exporting: false,
                dealers: null,
                teams: null
            }
        },
        computed: {
            filterFields() {
                const fields = [
                    { key: 'member', label: 'Member', placeholder: 'Name or member id' },
                    { key: 'partner', label: 'Partner', placeholder: 'Title or id' },
                    { key: 'team', label: 'Team', type: 'autocomplete', items: this.teams, itemText: 'title' }
                ]
                if (this.isAdmin) {
                    fields.push({ key: 'dealer', label: 'Dealer', type: 'autocomplete', items: this.dealers, itemText: fullName })
                }
                fields.push(
                    { key: 'scanCompleted', label: 'Scan completed', type: 'boolean' },
                    { key: 'from', label: 'Receipt date from', type: 'date' },
                    { key: 'to', label: 'Receipt date to', type: 'date' }
                )
                return fields
            },
            headers() {
                return [
                    { text: 'Receipt date', value: 'date' },
                    ...(this.isAdmin ? [{ text: 'Member', value: 'member' }] : []),
                    { text: 'PARON card', value: 'card' },
                    { text: 'Partner', value: 'partner' },
                    { text: 'Category', value: 'category' },
                    { text: 'Total', value: 'total', align: 'end' },
                    { text: 'Scan', value: 'scanCompleted', align: 'center' },
                    ...(this.isAdmin ? [{ text: '', value: 'actions', align: 'end', sortable: false, width: 96 }] : [])
                ]
            }
        },
        created() {
            teamService.getTeamOptions().then(resp => {
                this.teams = resp.data.data
            }).catch(() => {
                this.teams = []
            })
            if (this.isAdmin) {
                dealerService.getAllDealers().then(resp => {
                    this.dealers = resp.data.data
                }).catch(() => {
                    this.dealers = []
                })
            }
        },
        methods: {
            formatDate,
            formatCurrency,
            fetchPage({ page, perPage, filters }) {
                return transactionService.getTransactionsByFilter(page, filters, perPage)
            },
            onPageLoaded(data) {
                this.sum = Number(data.receipts_total_sum) || 0
            },
            deleteTransaction: id => transactionService.deleteTransactionById(id),
            memberName(member) {
                return member && typeof member === 'object' ? fullName(member) : ''
            },
            paronCards(member) {
                return member ? activeCardNumbers(member.cards, 'PARON').join(', ') : ''
            },
            exportTransactions() {
                this.exporting = true
                transactionService.exportTransactionsByFilter(this.requestFilters()).then(resp => {
                    saveBlob(resp.data, `transactions_${dateStamp()}.csv`)
                }).catch(error => {
                    this.$notify.error(error, 'Could not export the transactions')
                }).finally(() => {
                    this.exporting = false
                })
            }
        }
    }
</script>

<style scoped>
.stat-card {
    padding: 14px 18px;
}

.stat-label {
    font-size: 0.8rem;
    color: rgba(0, 0, 0, 0.6);
}

.stat-value {
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--v-primary-base);
}
</style>
